
import { defineComponent, ref, onMounted, watch, onBeforeUnmount, computed } from 'vue'
import * as echarts from 'echarts'
import type { ECharts } from 'echarts'
import type { TimeRange, PerformanceData, ColorMode } from '@/types/arkOne'
import { filterDataByTimeRange, getChartXAxisLabels, formatPercentage, getValueColor } from '@/utils/arkOneUtils'
import Nodata from '@/components/page/nodata.vue'

const __default__ = defineComponent({
  name: 'PerformanceChart',
  components: {
    Nodata
  },
  props: {
    // 图表数据（统一使用旧格式 PerformanceData 数组）
    data: {
      type: Array as () => PerformanceData[],
      required: false,
      default: () => []
    },
    timeRange: {
      type: String as () => TimeRange,
      default: '1Y'
    },
    rebalancingDates: {
      type: Array as () => string[],
      default: () => []
    },
    // 业绩指标相关
    annualizedReturn: {
      type: Number,
      default: 0
    },
    totalReturn: {
      type: Number,
      default: 0
    },
    totalDividendYield: {
      type: Number,
      default: 0
    },
    colorMode: {
      type: String as () => ColorMode,
      default: 'red-rise-green-fall'
    },
    // 主题：'dark' | 'light'
    theme: {
      type: String,
      default: 'dark'
    },
    // 语言支持：'zh-Hans' 简体中文，'zh-Hant' 繁体中文
    language: {
      type: String,
      default: 'zh-Hans'
    }
  },
  emits: ['timeRangeChange', 'rebalancingMarkerClick'],
  setup(props, { emit }) {
    const chartRef = ref<HTMLElement | null>(null)
    let chartInstance: ECharts | null = null
    const selectedRange = ref<TimeRange>(props.timeRange)
    const crosshairMode = ref(false)

    // 语言相关
    const isTraditional = computed(() => props.language === 'zh-Hant')

    const tran = (text: string): string => {
      if (!isTraditional.value) return text
      // lang.js 在 String.prototype 上挂了 tran
      return (text as any).tran ? (text as any).tran() : text
    }
    
    console.log('[PerformanceChart] props.language:', props.language)
    console.log('[PerformanceChart] isTraditional:', isTraditional.value)
    
    // 翻译函数
    const t = (key: string): string => {
      const translations: Record<string, Record<string, string>> = {
        'performanceChart': {
          'title': isTraditional.value ? '模擬業績走勢' : '模拟业绩走势',
          'annualizedReturn': isTraditional.value ? '年化收益率' : '年化收益率',
          'totalReturn': isTraditional.value ? '綜合收益率' : '综合收益率',
          'totalDividendYield': isTraditional.value ? '綜合股息率' : '综合股息率',
          'noData': isTraditional.value ? '暫無業績數據' : '暂无业绩数据'
        },
        'timeRange': {
          '1M': isTraditional.value ? '近1月' : '近1月',
          '3M': isTraditional.value ? '近3月' : '近3月',
          '6M': isTraditional.value ? '近半年' : '近半年',
          '1Y': isTraditional.value ? '近1年' : '近1年',
          'ALL': isTraditional.value ? '全部' : '全部'
        }
      }
      
      const [category, subKey] = key.split('.')
      const result = translations[category]?.[subKey] || key
      return result
    }

    // 是否有可展示的指标（至少一个非零）
    const hasMetrics = computed(() => {
      return (
        props.annualizedReturn !== 0 ||
        props.totalReturn !== 0 ||
        props.totalDividendYield !== 0
      )
    })

    const formattedAnnualized = computed(() => formatPercentage(props.annualizedReturn))
    const formattedTotal = computed(() => formatPercentage(props.totalReturn))
    const formattedDividend = computed(() => formatPercentage(props.totalDividendYield))

    const annualizedColor = computed(() => getValueColor(props.annualizedReturn, props.colorMode))
    const totalColor = computed(() => getValueColor(props.totalReturn, props.colorMode))
    const dividendColor = computed(() => getValueColor(props.totalDividendYield, props.colorMode))

    const timeRanges = [
      { label: t('timeRange.1M'), value: '1M' as TimeRange },
      { label: t('timeRange.3M'), value: '3M' as TimeRange },
      { label: t('timeRange.6M'), value: '6M' as TimeRange },
      { label: t('timeRange.1Y'), value: '1Y' as TimeRange },
      { label: t('timeRange.ALL'), value: 'ALL' as TimeRange }
    ]

    // 处理时间范围变化
    const handleRangeChange = (range: TimeRange) => {
      selectedRange.value = range
      emit('timeRangeChange', range)
    }

    // 双击切换十字光标模式
    const handleDblClick = () => {
      crosshairMode.value = !crosshairMode.value
      updateChart()
    }

    // 初始化图表
    const initChart = () => {
      if (!chartRef.value) return

      chartInstance = echarts.init(chartRef.value)

      // 监听点击事件（点击红点）
      chartInstance.on('click', (params: any) => {
        if (params.componentType === 'markPoint') {
          const date = params.data.coord[0]
          emit('rebalancingMarkerClick', date)
        }
      })

      // 在 DOM 上监听双击，覆盖整个图表区域
      chartRef.value.addEventListener('dblclick', handleDblClick)

      updateChart()
    }

    // 更新图表
    const updateChart = () => {
      if (!chartInstance) return

      let dates: string[] = []
      let returns: number[] = []
      let rebalancingMarkers: any[] = []
      let filteredData: PerformanceData[] = []

      if (props.data && props.data.length > 0) {
        // 使用 PerformanceData 数组
        filteredData = filterDataByTimeRange(props.data, selectedRange.value)
        
        if (filteredData.length === 0) return

        dates = filteredData.map(item => item.date)
        returns = filteredData.map(item => item.returnRate)

        // 找出调仓日期的数据点
        rebalancingMarkers = filteredData
          .filter(item => item.rebalancing)
          .map(item => ({
            name: item.date,
            coord: [item.date, item.returnRate],
            value: item.rebalancing?.adjustments.map(adj => 
              `${adj.stockName} ${adj.direction}`
            ).join('\n') || '',
            itemStyle: { color: '#ff4d4f' }
          }))
      } else {
        // 没有有效数据
        return
      }

      // 只有一个数据点时，补点用于画水平线并居中展示
      // 使用三个相同点，让中间点居中，首尾也都是同一日期
      let singlePointCenter: number | null = null
      if (dates.length === 1 && returns.length === 1) {
        singlePointCenter = returns[0]
        dates = [dates[0], dates[0], dates[0]]
        returns = [returns[0], returns[0], returns[0]]
      }

      const isSinglePadded =
        dates.length === 3 && dates[0] === dates[1] && dates[1] === dates[2] && singlePointCenter !== null

      const option: echarts.EChartsOption = {
        backgroundColor: props.theme === 'light' ? '#ffffff' : '#111424',
        tooltip: {
          trigger: 'axis',
          backgroundColor: props.theme === 'light' ? 'rgba(255, 255, 255, 0.95)' : 'rgba(0, 0, 0, 0.8)',
          borderColor: props.theme === 'light' ? '#e0e0e0' : '#333',
          borderWidth: 1,
          textStyle: {
            color: props.theme === 'light' ? '#333' : '#fff'
          },
          confine: true,  // 限制 tooltip 在图表区域内
          appendToBody: true,  // 将 tooltip 添加到 body，避免被遮挡
          axisPointer: {
            type: crosshairMode.value ? 'cross' : 'line',
            lineStyle: {
              color: '#666',
              type: 'dashed'
            }
          },
          formatter: (params: any) => {
            if (!Array.isArray(params)) return ''
            
            const date = params[0].axisValue
            const isLight = props.theme === 'light'
            const textColor = isLight ? '#333' : '#fff'
            const subColor = isLight ? '#888' : '#999'
            const borderColor = isLight ? '#ddd' : '#444'
            let html = `<div style="padding: 8px;">
              <div style="margin-bottom: 8px; font-weight: bold; color: ${textColor};">${date}</div>`
            
            params.forEach((param: any) => {
              const value = param.value
              const sign = value >= 0 ? '+' : ''
              const color = value >= 0 ? '#f5222d' : '#52c41a'
              html += `<div style="margin-bottom: 4px;">
                <span style="color: ${param.color};">${param.seriesName}:</span>
                <span style="color: ${color}; font-weight: bold; margin-left: 8px;">${sign}${value.toFixed(2)}%</span>
              </div>`
            })

            // 显示调仓信息（统一从 filteredData 中查找）
            if (filteredData.length > 0) {
              // 旧格式：从 filteredData 中查找
              const dataPoint = filteredData.find(item => item.date === date)
              if (dataPoint?.rebalancing) {
                html += `<div style="margin-top: 8px; padding-top: 8px; border-top: 1px solid ${borderColor};">`
                html += `<div style="color: ${subColor}; margin-bottom: 4px;">${tran('调仓记录：')}</div>`
                dataPoint.rebalancing.adjustments.forEach(adj => {
                  const direction = String(adj.direction || '')
                  const directionColor = direction === '调入' || direction === '調入' ? '#f5222d' : '#52c41a'
                  html += `<div style="margin-bottom: 2px;">
                    <span style="color: ${textColor};">${adj.stockName}</span>
                    <span style="color: ${directionColor}; margin-left: 8px;">${tran(direction)}</span>
                  </div>`
                })
                html += '</div>'
              }
            }

            html += '</div>'
            return html
          }
        },
        legend: {
          show: false
        },
        grid: {
          left: '2%',
          right: '4%',
          bottom: '8%',
          top: '5%',
          containLabel: true
        },
        xAxis: {
          type: 'category',
          data: dates,
          boundaryGap: false,
          axisLabel: {
            color: '#666',
            fontSize: 11,
            formatter: (value: string, index: number) => {
              // 单个日期补成三个相同类目时，头尾都显示日期
              if (dates.length === 3 && dates[0] === dates[1] && dates[1] === dates[2]) {
                return index === 0 || index === 2 ? value : ''
              }
              // 单点补齐为2个相同类目时，隐藏第二个重复标签
              if (dates.length === 2 && dates[0] === dates[1]) {
                return index === 0 ? value : ''
              }
              // 根据数据点数量智能显示标签
              const totalDates = dates.length
              
              // 如果数据点少于等于10个，显示所有日期
              if (totalDates <= 10) {
                return value
              }
              
              // 如果数据点在10-30个之间，显示约5个标签
              if (totalDates <= 30) {
                const step = Math.floor(totalDates / 5)
                if (index % step === 0 || index === totalDates - 1) {
                  return value
                }
                return ''
              }
              
              // 如果数据点超过30个，只显示首、中、尾3个标签
              if (index === 0 || index === Math.floor(totalDates / 2) || index === totalDates - 1) {
                return value
              }
              return ''
            },
            margin: 10,
            rotate: dates.length > 10 ? 0 : 0  // 可以根据需要旋转标签
          },
          axisLine: { 
            show: true,
            lineStyle: { 
              color: props.theme === 'light'
                ? '#d9d9d9'
                : 'rgba(255, 255, 255, 0.12)'
            } 
          },
          axisTick: { show: false },
          splitLine: { show: false }
        },
        yAxis: {
          type: 'value',
          axisLabel: {
            color: '#666',
            fontSize: 11,
            formatter: '{value}%'
          },
          // 单点场景下，让水平线在 Y 轴中间附近
          ...(isSinglePadded && singlePointCenter !== null
            ? (() => {
                const center = singlePointCenter as number
                const padding = Math.max(Math.abs(center) * 0.2, 5)
                return {
                  min: center - padding,
                  max: center + padding
                }
              })()
            : {}),
          axisLine: { show: false },
          axisTick: { show: false },
          splitLine: { 
            show: true,
            lineStyle: { 
              color: props.theme === 'light'
                ? '#e5e5e5'
                : 'rgba(255, 255, 255, 0.06)',
              type: 'solid',
              width: 1
            } 
          },
          name: tran('持仓比例'),
          nameTextStyle: {
            color: '#666',
            fontSize: 11
          }
        },
        series: [
          {
            name: tran('本策略'),
            type: 'line',
            data: returns,
            smooth: true,
            symbol: 'none',
            lineStyle: { 
              color: '#1890ff', 
              width: 1.5
            },
            areaStyle: {
              color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
                { offset: 0, color: 'rgba(24, 144, 255, 0.2)' },
                { offset: 1, color: 'rgba(24, 144, 255, 0)' }
              ])
            },
            markPoint: {
              symbol: 'circle',
              symbolSize: 6,
              data: rebalancingMarkers,
              label: { show: false },
              itemStyle: {
                color: '#ff4d4f',
                borderColor: '#ff4d4f',
                borderWidth: 1.5
              }
            }
          }
        ]
      }

      // 默认 setOption 会做合并，数据变短时可能残留旧的 xAxis/series
      // 这里改为全量替换，保证只展示当前区间的数据
      chartInstance.clear()
      chartInstance.setOption(option, { notMerge: true, lazyUpdate: false })
    }

    onMounted(() => {
      initChart()
      window.addEventListener('resize', () => {
        chartInstance?.resize()
      })
    })

    onBeforeUnmount(() => {
      if (chartRef.value) {
        chartRef.value.removeEventListener('dblclick', handleDblClick)
      }
      chartInstance?.dispose()
    })

    // 监听数据变化
    watch(() => props.data, () => {
      updateChart()
    }, { deep: true })

    watch(() => selectedRange.value, () => {
      updateChart()
    })

    return {
      chartRef,
      selectedRange,
      timeRanges,
      handleRangeChange,
      hasMetrics,
      formattedAnnualized,
      formattedTotal,
      formattedDividend,
      annualizedColor,
      totalColor,
      dividendColor,
      t
    }
  }
})

import { useCssVars as _useCssVars } from 'vue'
const __injectCSSVars__ = () => {
_useCssVars(_ctx => ({
  "5264026a": (_ctx.theme === "light" ? "#ffffff" : "#111424"),
  "485625f0": (_ctx.theme === "light" ? "#000000" : "#FFFFFF"),
  "2a49208e": (_ctx.theme === "light" ? "#636363" : "#999999"),
  "4c92cf83": (_ctx.theme === "light" ? "#f0f2f5" : "rgba(26, 26, 26, 0.8)"),
  "884209e0": (_ctx.theme === "light" ? "#d9d9d9" : "rgba(45, 45, 45, 0.6)"),
  "fa3fe6e0": (_ctx.theme === "light" ? "#666666" : "#999999"),
  "49763fc2": (_ctx.theme === "light" ? "#e6f7ff" : "rgba(40, 40, 40, 0.9)"),
  "b7b1851c": (_ctx.theme === "light" ? "#1890ff" : "#bbbbbb"),
  "fd2c07e2": (_ctx.theme === "light" ? "#1890ff" : "#2C344E")
}))}
const __setup__ = __default__.setup
__default__.setup = __setup__
  ? (props, ctx) => { __injectCSSVars__();return __setup__(props, ctx) }
  : __injectCSSVars__

export default __default__